import React, { useState, useEffect } from "react";
import { Col, Row, Nav, Tab, Card } from "react-bootstrap";
import RaiseTicketModal from "./RaiseTicketModal";
import TicketListingWrapper from "./TicketListingWrapper";
import TicketDetailsModal from "./TicketDetailsModal";
import RoleSpecificStats from "./RoleSpecificStats";
import SupervisorDashboard from "./SupervisorDashboard";
import AdminDashboard from "./AdminDashboard";

import { useDispatch, useSelector } from "react-redux";
import {
  fetchCategoryDistribution,
  fetchGymwiseStats,
  fetchInfraSpend,
  fetchPaidResolutionTracker,
  fetchPriorityHeatmap,
  fetchSupervisorPerformance,
  fetchSupervisors,
  fetchTatTrend,
  fetchTicketsData,
  fetchTicketStatsData,
} from "../../redux/reducers/ticketSystemSlice";
import { selectToken } from "../auth/auth.selector";
import "../../styles/ticket-system/fd-dashboard.css";
import { toast } from "react-toastify";
import { fetchApiData } from "../../redux/reducers/commonApiSlice";
import { exportTickets } from "../../redux/ticket-system/ticketSystemAPIs";
import {
  ticketSystemCategories,
  ticketSystemStartWork,
} from "../../data/ApiEndpoints";
import CommonStructuredV2 from "../common-components/CommonStructuredV2";
import FilterContainer from "../common-components/filters/filter-container";
import DateRangerFilter from "../common-components/filters/date-ranger-filter";
import GymFilter from "../common-components/filters/gym-filter";
import GenericSelectFilter from "../common-components/filters/generic-select-filter";
import { getFiltersForRole } from "./utils/getFilterRole";
import {
  PRIORITY_OPTIONS,
  STATUS_OPTIONS,
  BREACHED_OPTIONS,
  TICKET_TYPE_OPTIONS,
} from "./utils/ticketExports";
import {
  ADMIN_TABS,
  MANAGER_TABS,
  RECEPTIONIST_TABS,
  SUPERVISOR_TABS,
} from "./utils/ticketRoleWiseTabs";
import { getTicketRoles } from "./utils/getTicketRoles";

const TicketDashboardLanding = ({ userInfoData }) => {
  const dispatch = useDispatch();
  const token = useSelector(selectToken);

  const { isManager, isSupervisor, isReceptionist, isAdmin, isClusterManager } =
    getTicketRoles(userInfoData);

  // Use centralized Redux state
  const {
    ticketsLoading,
    ticketsData,
    ticketStats,
    supervisors,
    ticketsPagination,
  } = useSelector((state) => state.ticketSystem);

  // Common state
  const [isRaiseModalOpen, setIsRaiseModalOpen] = useState(false);
  const [selectedTicket, setSelectedTicket] = useState(null);
  const [isTicketDetailsOpen, setIsTicketDetailsOpen] = useState(false);
  const [workingOnTickets, setWorkingOnTickets] = useState({});

  // Cluster manager behaves like admin but with gym restrictions
  const isAdminLike = isAdmin || isClusterManager;

  // Get available tabs based on role
  const getAvailableTabs = () => {
    if (isSupervisor) return SUPERVISOR_TABS;
    if (isManager) return MANAGER_TABS;
    if (isReceptionist) return RECEPTIONIST_TABS;
    if (isAdminLike) return ADMIN_TABS; // Both admin and cluster manager use admin tabs
    return {};
  };

  // console.log("userInfoData", userInfoData);

  // Get initial active tab based on role
  const getInitialActiveTab = () => {
    if (isSupervisor) return "assigned";
    if (isManager) return "assignment";
    if (isReceptionist) return "my_tickets";
    if (isAdminLike) return "analytics"; // Both admin and cluster manager start with analytics
    return "all"; // Default
  };

  // Tab state
  const [activeTab, setActiveTab] = useState(getInitialActiveTab());
  const availableTabs = getAvailableTabs();

  // Get initial status filter based on active tab
  const getInitialStatusFilter = () => {
    const initialTab = getInitialActiveTab();
    return availableTabs[initialTab]?.status || "";
  };

  // Filter states - now passed to API
  const [searchTerm, setSearchTerm] = useState("");
  const [statusFilter, setStatusFilter] = useState(getInitialStatusFilter());
  const [priorityFilter, setPriorityFilter] = useState("");
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [currentPage, setCurrentPage] = useState(0);
  const [pageLimit, setPageLimit] = useState(10);

  // New filter states
  const [issueTypeFilter, setIssueTypeFilter] = useState(null);
  // const [categoryFilter, setCategoryFilter] = useState(null);
  const [issueFilter, setIssueFilter] = useState(null);
  const [ticketTypeFilter, setTicketTypeFilter] = useState(null);
  const [isExporting, setIsExporting] = useState(false);
  const [raisedByFilter, setRaisedByFilter] = useState(null);
  const [supervisorFilter, setSupervisorFilter] = useState(null);
  const [gymFilter, setGymFilter] = useState(null);
  const [breachedFilter, setBreachedFilter] = useState(null);

  // Handle breached filter change
  const handleBreachedFilterChange = (value) => {
    setBreachedFilter(value);
    setCurrentPage(0);

    // If user selects "Breached" (value: "1"), switch to SLA breached tab if available
    if (value && (value.value === "1" || value === "1")) {
      const slaTab = availableTabs["sla_breached"];
      if (slaTab) {
        console.log("Switching to SLA breached tab from breached filter");
        setActiveTab("sla_breached");
      }
    }
  };

  // Filter options states
  const [issueTypeOptions, setIssueTypeOptions] = useState([]);
  // const [categoryOptions, setCategoryOptions] = useState([]);
  const [issueOptions, setIssueOptions] = useState([]);
  const [raisedByOptions, setRaisedByOptions] = useState([]);

  // Fetch filter options
  const fetchFilterOptions = async () => {
    if (!token) return;

    try {
      // Fetch categories for issue type filter
      const issueResult = await dispatch(
        fetchApiData({
          method: "GET",
          endpoint: "/ticketSystem/issue-type?limit=100&page=1",
          // endpoint: ticketSystemCategories,
          token,
          payload: {
            filters: {},
          },
        })
      );

      if (issueResult?.payload?.status) {
        // Create issue type options from categories and subcategories
        const issues =
          issueResult?.payload?.data
            ?.filter((item) => item.status === "active")
            .map((item) => ({
              label: item.name,
              value: item.uid,
            })) || [];

        setIssueOptions(issues);
      }

      // Fetch users for raised by filter (only for admin, cluster manager and manager)
      if (isAdminLike || isManager) {
        let endpoint = "/ticketSystem/users?role=receptionist";

        // For cluster managers, filter by their assigned gym
        if (isClusterManager && userInfoData?.gym_id) {
          endpoint += `&gym_id=${userInfoData.gym_id}`;
        }

        const usersResult = await dispatch(
          fetchApiData({
            method: "GET",
            endpoint,
            token,
          })
        );

        if (usersResult?.payload?.status && usersResult?.payload?.result) {
          const raisedByOpts = usersResult.payload.result.map((user) => ({
            label: user.name,
            value: user.uid || user.id,
          }));
          setRaisedByOptions(raisedByOpts);
        }
      }
    } catch (error) {
      console.error("Error fetching filter options:", error);
    }
  };

  // Fetch filter options on component mount
  useEffect(() => {
    fetchFilterOptions();
    dispatch(fetchSupervisors(userInfoData?.token));
  }, [userInfoData?.token]);

  // Handle tab change
  const handleTabChange = (tab) => {
    setActiveTab(tab);
    setCurrentPage(0); // Reset to first page when changing tabs
    // Update status filter based on tab configuration
    const newStatus = availableTabs[tab]?.status;
    setStatusFilter(newStatus || ""); // Set to empty string instead of null for "all" tab

    // Also update breached filter if switching to/from SLA breached tab
    if (tab === "sla_breached") {
      setBreachedFilter({ label: "Breached", value: "1" });
    } else if (
      breachedFilter &&
      (breachedFilter.value === "1" || breachedFilter === "1")
    ) {
      // Reset breached filter if switching away from SLA breached tab
      setBreachedFilter(null);
    }

    // Reset supervisor filter when manager switches to personal tabs
    if (
      isManager &&
      (tab === "my_assigned" ||
        tab === "my_in_progress" ||
        tab === "my_completed")
    ) {
      setSupervisorFilter(null);
    }
  };

  // Reset all filters
  const handleResetFilters = () => {
    setSearchTerm("");
    setStatusFilter("");
    setPriorityFilter("");
    setStartDate("");
    setEndDate("");
    setIssueTypeFilter(null);
    // setCategoryFilter(null);
    setRaisedByFilter(null);
    setSupervisorFilter(null);
    setGymFilter(null);
    setBreachedFilter(null);
    setCurrentPage(0);
  };

  // Fetch tickets from Redux with backend filtering for all users
  // Phase 1: export the currently-filtered tickets as CSV (mirrors the filters
  // applied in fetchTickets, minus pagination). Backend returns a CSV file when
  // is_export=1, or a JSON message for "no data"/"not allowed".
  const handleExport = async () => {
    if (!token) return;
    try {
      setIsExporting(true);
      const filters = {};
      if (searchTerm.trim()) filters.search = searchTerm.trim();
      if (statusFilter) {
        if (statusFilter === "sla_breached") filters.sla_breached = "1";
        else filters.status = statusFilter;
      }
      if (priorityFilter) filters.priority = priorityFilter.value || priorityFilter;
      if (breachedFilter) filters.sla_breached = breachedFilter.value || breachedFilter;
      if (startDate) filters.created_date_from = startDate;
      if (endDate) filters.created_date_to = endDate;
      if (issueFilter) filters.issue_type = issueFilter.value;
      if (ticketTypeFilter && ticketTypeFilter.value)
        filters.ticket_type = ticketTypeFilter.value;
      if (issueTypeFilter?.value?.startsWith("category_"))
        filters.category_id = issueTypeFilter.value.replace("category_", "");
      if (issueTypeFilter?.value?.startsWith("subcategory_"))
        filters.sub_category_id = issueTypeFilter.value.replace("subcategory_", "");
      if (gymFilter) filters.gym_id = gymFilter.value;
      // Role scoping (same as fetchTickets)
      if (isReceptionist) {
        filters.created_by = userInfoData?.uid || userInfoData?.user_id;
        filters.gym_id = userInfoData?.gym_id;
      }
      if (isSupervisor) filters.assigned_to = userInfoData?.uid || userInfoData?.user_id;
      if (isClusterManager) filters.gym_id = userInfoData?.gym_id;

      const result = await exportTickets(filters, token);
      if (result.kind === "csv") {
        const url = window.URL.createObjectURL(result.blob);
        const a = document.createElement("a");
        a.href = url;
        a.download = result.filename || "tickets.csv";
        document.body.appendChild(a);
        a.click();
        a.remove();
        window.URL.revokeObjectURL(url);
        toast.success("Tickets exported");
      } else {
        toast.error(result.message || "No data to export");
      }
    } catch (e) {
      toast.error("Export failed");
    } finally {
      setIsExporting(false);
    }
  };

  const fetchTickets = () => {
    if (token) {
      const filters = {
        limit: pageLimit,
        page: currentPage + 1,
      };

      // Add search term if provided
      if (searchTerm.trim()) {
        filters.search = searchTerm.trim();
      }

      // Add status filter - use statusFilter directly instead of tab status
      if (statusFilter) {
        if (statusFilter === "sla_breached") {
          filters.sla_breached = "1";
        } else if (
          activeTab === "assignment" &&
          statusFilter === "opened" &&
          isManager
        ) {
          filters.status = "opened,re_opened";
        } else {
          filters.status = statusFilter;
        }
      }

      // Add role-specific filters
      if (isSupervisor) {
        filters.assigned_to = userInfoData?.uid || userInfoData?.user_id;
      }

      // if (
      //   isManager &&
      //   (activeTab === "my_assigned" ||
      //     activeTab === "my_in_progress" ||
      //     activeTab === "my_completed")
      // ) {
      //   filters.assigned_to = userInfoData?.uid || userInfoData?.user_id;
      // }

      if (isReceptionist) {
        filters.created_by = userInfoData?.uid || userInfoData?.user_id;
      }

      // Add gym restrictions for cluster managers
      if (isClusterManager || isReceptionist) {
        filters.gym_id = userInfoData?.gym_id;
      }

      // Add priority filter if provided
      if (priorityFilter) {
        filters.priority = priorityFilter.value || priorityFilter;
      }

      // Add date range filters if provided
      if (startDate) {
        filters.created_date_from = startDate;
      }
      if (endDate) {
        filters.created_date_to = endDate;
      }

      // Add new filters
      if (issueTypeFilter) {
        // Check if it's a category or subcategory
        if (issueTypeFilter.value.startsWith("category_")) {
          filters.category_id = issueTypeFilter.value.replace("category_", "");
        } else if (issueTypeFilter.value.startsWith("subcategory_")) {
          filters.sub_category_id = issueTypeFilter.value.replace(
            "subcategory_",
            ""
          );
        }
      }

      if (issueFilter) {
        filters.issue_type = issueFilter.value;
      }

      if (ticketTypeFilter && ticketTypeFilter.value) {
        filters.ticket_type = ticketTypeFilter.value;
      }

      if (raisedByFilter) {
        filters.created_by = raisedByFilter.value;
      }

      if (gymFilter) {
        filters.gym_id = gymFilter.value;
      }

      // if (supervisorFilter) {
      //   filters.assigned_to = supervisorFilter.value;
      // }

      if (breachedFilter) {
        filters.sla_breached = breachedFilter.value || breachedFilter;
      }

      const ticketFilters = {
        ...filters,
        assigned_to: isManager
          ? activeTab === "my_assigned" ||
            activeTab === "my_in_progress" ||
            activeTab === "my_completed"
            ? userInfoData?.uid || userInfoData?.user_id
            : supervisorFilter?.value || ""
          : isSupervisor
          ? userInfoData?.uid || userInfoData?.user_id
          : supervisorFilter?.value || "",
      };

      dispatch(
        fetchTicketsData({
          filters: ticketFilters,
          token,
        })
      );

      const statsFilters = {
        ...filters,
        status: "",
        // status: statusFilter || "",
        logged_in_user: isManager
          ? userInfoData?.uid || userInfoData?.user_id
          : "",
        assigned_to: isSupervisor
          ? userInfoData?.uid || userInfoData?.user_id
          : supervisorFilter?.value || "",
      };

      dispatch(
        fetchTicketStatsData({
          stats_type: isClusterManager
            ? "admin"
            : userInfoData?.ticket_account_type,
          token,
          filters: statsFilters,
        })
      );

      if (isAdminLike) {
        dispatch(fetchGymwiseStats({ token, filters }));
        dispatch(fetchCategoryDistribution({ token, filters }));
        dispatch(fetchInfraSpend({ token, filters }));
        dispatch(fetchTatTrend({ token, filters }));
        dispatch(fetchSupervisorPerformance({ token, filters }));
        dispatch(fetchPaidResolutionTracker({ token, filters }));
        dispatch(fetchPriorityHeatmap({ token, filters }));
      }
    }
  };

  // Initialize status filter when component mounts and userInfoData is available
  useEffect(() => {
    if (userInfoData?.ticket_account_type) {
      const initialStatus = getInitialStatusFilter();

      if (initialStatus !== statusFilter) {
        setStatusFilter(initialStatus);
      }
    }
  }, [userInfoData?.ticket_account_type]);

  // Refetch when filters change (debounced for search)
  useEffect(() => {
    if (token) {
      const timeoutId = setTimeout(
        () => {
          setCurrentPage(0); // Reset to first page when filters change
          fetchTickets();
          // fetchTicketStatsFunc(); // Also refresh stats when filters change
        },
        searchTerm ? 300 : 0
      ); // 300ms debounce for search, immediate for others

      return () => clearTimeout(timeoutId);
    }
  }, [
    searchTerm,
    statusFilter,
    priorityFilter,
    startDate,
    endDate,
    issueTypeFilter,
    issueFilter,
    ticketTypeFilter,
    // categoryFilter,
    raisedByFilter,
    supervisorFilter,
    gymFilter,
    breachedFilter,
    activeTab,
    token,
    userInfoData?.gym_id,
  ]);

  // Category cascade: when an Issue Type is selected, fetch its categories so the
  // Category dropdown can populate. Values are prefixed `category_<uid>` so the
  // existing issueTypeFilter apply-logic maps them to filters.category_id.
  useEffect(() => {
    const loadCategories = async () => {
      // Reset any previously selected category when issue type changes.
      setIssueTypeFilter(null);
      if (!issueFilter?.value || !token) {
        setIssueTypeOptions([]);
        return;
      }
      try {
        const catRes = await dispatch(
          fetchApiData({
            method: "GET",
            endpoint: `/ticketSystem/category?limit=100&page=1&issue_type_id=${issueFilter.value}`,
            token,
            payload: { filters: {} },
          })
        );
        if (catRes?.payload?.status) {
          const cats = (catRes.payload.data || [])
            .filter((c) => c.status === "active")
            .map((c) => ({ label: c.name, value: `category_${c.uid}` }));
          setIssueTypeOptions(cats);
        } else {
          setIssueTypeOptions([]);
        }
      } catch (e) {
        setIssueTypeOptions([]);
      }
    };
    loadCategories();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [issueFilter, token]);

  useEffect(() => {
    fetchTickets();
  }, [pageLimit, currentPage]);

  // Handle filter changes
  const handleSearchChange = (value) => {
    setSearchTerm(value);
  };

  // Handle status filter change
  const handleStatusFilterChange = (value) => {
    // Handle both string (from tab) and object (from react-select) formats
    const filterValue =
      typeof value === "object" && value !== null ? value.value : value;
    setStatusFilter(filterValue);
    setCurrentPage(0);

    // Get the appropriate fallback tab for this role
    const getFallbackTab = () => {
      if (isReceptionist) return "my_tickets"; // Receptionists don't have "all" tab
      if (isSupervisor || isManager || isAdminLike) return "all"; // Others have "all" tab
      return "all"; // Default fallback
    };

    const fallbackTab = getFallbackTab();
    // console.log("Fallback tab:", fallbackTab);

    // If no status selected, switch to fallback tab if current tab has specific status
    if (!filterValue || filterValue === "" || filterValue === null) {
      const currentTab = availableTabs[activeTab];
      if (
        currentTab &&
        currentTab.status &&
        currentTab.status !== null &&
        currentTab.status !== ""
      ) {
        console.log(
          `No status selected, switching from ${activeTab} to ${fallbackTab}`
        );
        setActiveTab(fallbackTab);
      }
      return;
    }

    // Handle special case for SLA breached
    if (filterValue === "sla_breached") {
      const slaTab = availableTabs["sla_breached"];
      if (slaTab) {
        console.log("Switching to SLA breached tab");
        setActiveTab("sla_breached");
      } else {
        console.log(
          `SLA breached tab not available, switching to ${fallbackTab}`
        );
        setActiveTab(fallbackTab);
      }
      return;
    }

    // Handle status mappings
    let statusToMatch = filterValue;
    if (filterValue === "re_opened") {
      statusToMatch = "reopened";
    }

    if (
      isManager &&
      (filterValue === "assigned" ||
        filterValue === "work_completed" ||
        filterValue === "work_started")
    ) {
      setActiveTab(fallbackTab);
      return;
    }

    // Find tab with matching status
    const matchingTab = Object.values(availableTabs).find(
      (tab) => tab.status === statusToMatch
    );

    if (matchingTab) {
      console.log(
        `Found matching tab: ${matchingTab.key} for status: ${statusToMatch}`
      );
      setActiveTab(matchingTab.key);
    } else {
      // Check if current tab matches the selected status
      const currentTab = availableTabs[activeTab];
      if (currentTab && currentTab.status === statusToMatch) {
        console.log(
          `Current tab ${activeTab} already matches status: ${statusToMatch}`
        );
        // Stay on current tab
      } else {
        console.log(
          `No matching tab found for status: ${filterValue}, switching to ${fallbackTab}`
        );
        setActiveTab(fallbackTab);
      }
    }
  };

  const handlePriorityFilterChange = (value) => {
    // Handle react-select object format
    setPriorityFilter(value);
  };

  // Handle date range change
  const handleDateRangeChange = (start, end) => {
    setStartDate(start);
    setEndDate(end);
    setCurrentPage(0); // Reset to first page when date filter changes
  };

  const handleCreateTicket = () => {
    setIsRaiseModalOpen(true);
  };

  const handleCloseRaiseModal = () => {
    setIsRaiseModalOpen(false);
  };

  const handleTicketSubmit = (ticketData) => {
    // Refresh tickets using Redux with current filters
    fetchTickets(currentPage);
    setIsRaiseModalOpen(false);
  };

  const handleTicketCardClick = (ticket) => {
    setSelectedTicket(ticket);
    setIsTicketDetailsOpen(true);
    // Don't auto-scroll for regular ticket clicks
    setScrollToVerification(false);
  };

  const handleCloseTicketDetails = () => {
    setIsTicketDetailsOpen(false);
    setSelectedTicket(null);
    setScrollToVerification(false); // Reset scroll flag
  };

  const handleTicketUpdate = (ticketId, action) => {
    // Refresh tickets using Redux with current filters
    fetchTickets(currentPage);
  };

  const handleReload = () => {
    fetchTickets(currentPage);
    // fetchTicketStatsFunc();
  };

  // Handle start work
  const handleStartWork = async (ticketId) => {
    setWorkingOnTickets((prev) => ({ ...prev, [ticketId]: "starting" }));

    try {
      const result = await dispatch(
        fetchApiData({
          method: "POST",
          endpoint: ticketSystemStartWork,
          payload: {
            ticket_id: ticketId,
            started_by: userInfoData?.uid || userInfoData?.user_id,
          },
          token,
        })
      );

      if (result?.payload?.status) {
        toast.success(result?.payload?.message || "Work started successfully");
        fetchTickets(currentPage);
        // fetchTicketStatsFunc();
      } else {
        toast.error(result?.payload?.message || "Failed to start work");
      }
    } catch (error) {
      console.error("Error starting work:", error);
      toast.error("Failed to start work");
    } finally {
      setWorkingOnTickets((prev) => ({ ...prev, [ticketId]: null }));
    }
  };

  // Handle complete work
  const handleCompleteWork = async (ticketId) => {
    fetchTickets(currentPage);
  };

  const handleWriteSuccess = () => {
    fetchTickets(currentPage);
  };

  // Handle manager verification (now handled within ticket details modal)
  const handleOpenVerificationModal = (ticket) => {
    setSelectedTicket(ticket);
    setIsTicketDetailsOpen(true);
  };

  const handleMarkAsResolved = (ticket) => {
    setSelectedTicket(ticket);
    setIsTicketDetailsOpen(true);
    setScrollToVerification(true);
  };

  // Add state for scroll to verification
  const [scrollToVerification, setScrollToVerification] = useState(false);

  // Common props object for TicketListingWrapper (without filters)
  const commonTicketListingProps = {
    tickets: ticketsData,
    userInfoData,
    isLoading: ticketsLoading,
    onTicketClick: handleTicketCardClick,
    onTicketAction: handleTicketUpdate,
    isPage: currentPage,
    setIsPage: (value) => setCurrentPage(value),
    pageLimit,
    setPageLimit,
    tablePagination: ticketsPagination,
    handleReload: handleReload,
  };

  // Get active filters based on role
  const activeFilters = getFiltersForRole(userInfoData);

  // Render filters component
  const renderFilters = () => {
    return (
      <Row className="mb-4">
        <Col>
          <div className="">
            <div className="mt-3">
              <FilterContainer
                onReset={handleResetFilters}
                onExport={handleExport}
                isExporting={isExporting}
              >
                {/* Search Filter */}
                {activeFilters.includes("search") && (
                  <div className="filter-search-wrapper">
                    <input
                      type="text"
                      placeholder="Search by ticket number or description..."
                      value={searchTerm}
                      onChange={(e) => handleSearchChange(e.target.value)}
                      className="premium-search-input"
                    />
                  </div>
                )}

                {/* Category Filter (cascades from selected Issue Type) */}
                {activeFilters.includes("category") &&
                  issueTypeOptions.length > 0 && (
                    <GenericSelectFilter
                      options={issueTypeOptions}
                      selectedOption={issueTypeFilter}
                      setSelectedOption={setIssueTypeFilter}
                      name="Category"
                      placeholder="Select Category"
                      isSearchable={true}
                    />
                  )}

                {/* Ticket Type Filter (Complaint / Requirement) */}
                {activeFilters.includes("ticketType") && (
                  <GenericSelectFilter
                    options={TICKET_TYPE_OPTIONS}
                    selectedOption={
                      typeof ticketTypeFilter === "string" && ticketTypeFilter
                        ? TICKET_TYPE_OPTIONS.find(
                            (opt) => opt.value === ticketTypeFilter
                          )
                        : ticketTypeFilter
                    }
                    setSelectedOption={setTicketTypeFilter}
                    name="Ticket Type"
                    placeholder="Select Ticket Type"
                    isSearchable={false}
                  />
                )}

                {/* Raised By Filter */}
                {activeFilters.includes("raisedBy") &&
                  raisedByOptions.length > 0 && (
                    <GenericSelectFilter
                      options={raisedByOptions}
                      selectedOption={raisedByFilter}
                      setSelectedOption={setRaisedByFilter}
                      name="Raised By"
                      placeholder="Select Raised By"
                      isSearchable={true}
                    />
                  )}

                {/* Supervisor Filter */}
                {activeFilters.includes("supervisor") && (
                  <GenericSelectFilter
                    options={[
                      { label: "All Supervisors", value: "" },
                      ...supervisors,
                    ]}
                    selectedOption={supervisorFilter}
                    setSelectedOption={setSupervisorFilter}
                    name="Supervisor"
                    placeholder="Select Supervisor"
                    isSearchable={true}
                  />
                )}

                {/* Status Filter - Only show for "all" tabs */}
                {activeFilters.includes("status") && (
                  <GenericSelectFilter
                    options={STATUS_OPTIONS}
                    selectedOption={
                      typeof statusFilter === "string" && statusFilter
                        ? STATUS_OPTIONS.find(
                            (opt) => opt.value === statusFilter
                          )
                        : statusFilter
                    }
                    setSelectedOption={handleStatusFilterChange}
                    name="Status"
                    placeholder="Select Status"
                    isSearchable={false}
                  />
                )}

                {/* Priority Filter */}
                {activeFilters.includes("priority") && (
                  <GenericSelectFilter
                    options={PRIORITY_OPTIONS}
                    selectedOption={
                      typeof priorityFilter === "string" && priorityFilter
                        ? PRIORITY_OPTIONS.find(
                            (opt) => opt.value === priorityFilter
                          )
                        : priorityFilter
                    }
                    setSelectedOption={handlePriorityFilterChange}
                    name="Priority"
                    placeholder="Select Priority"
                    isSearchable={false}
                  />
                )}

                {/* SLA Status Filter */}
                {activeFilters.includes("breached") && (
                  <GenericSelectFilter
                    options={BREACHED_OPTIONS}
                    selectedOption={
                      typeof breachedFilter === "string" && breachedFilter
                        ? BREACHED_OPTIONS.find(
                            (opt) => opt.value === breachedFilter
                          )
                        : breachedFilter
                    }
                    setSelectedOption={handleBreachedFilterChange}
                    name="SLA Status"
                    placeholder="Select SLA Status"
                    isSearchable={false}
                  />
                )}

                {/* Date Range Filter */}
                {activeFilters.includes("dateRange") && (
                  <DateRangerFilter
                    startDate={startDate}
                    endDate={endDate}
                    onDateChange={handleDateRangeChange}
                  />
                )}

                {/* Gym Filter */}
                {activeFilters.includes("gym") && (
                  <GymFilter
                    selectedGym={gymFilter}
                    setSelectedGym={setGymFilter}
                  />
                )}
                {/* Issue Type Filter */}
                {activeFilters.includes("issueFilter") &&
                  issueOptions.length > 0 && (
                    <GenericSelectFilter
                      options={issueOptions}
                      selectedOption={issueFilter}
                      setSelectedOption={setIssueFilter}
                      name="Issue Type"
                      placeholder="Select Issue Type"
                      isSearchable={true}
                    />
                  )}
              </FilterContainer>
            </div>
          </div>
        </Col>
      </Row>
    );
  };

  return (
    <div className="fd-dashboard-container">
      <Row className="ticket-system-heading-row">
        <Col>
          <CommonStructuredV2
            pageHeading={`Ticket System`}
            createBtnTxt={
              isManager || isSupervisor || isAdminLike
                ? null
                : "+ Raise New Facility Ticket"
            }
            onClickCreateBtn={handleCreateTicket}
            isReload={handleReload}
          />
        </Col>
      </Row>

      {/* Filters Section */}
      {renderFilters()}

      {/* Stats Cards Section */}
      <Row className="">
        <Col>
          <RoleSpecificStats
            userInfoData={userInfoData}
            ticketStats={ticketStats}
          />
        </Col>
      </Row>

      {/* Render tabs based on role */}
      {isSupervisor && (
        <Row>
          <Col>
            <Tab.Container activeKey={activeTab} onSelect={handleTabChange}>
              <Nav variant="tabs" className="mb-3">
                {Object.values(availableTabs)
                  .filter(
                    (tab) =>
                      !tab.showCondition || tab.showCondition(ticketStats)
                  )
                  .map((tab) => (
                    <Nav.Item key={tab.key}>
                      <Nav.Link
                        eventKey={tab.key}
                        className={
                          tab.key === "sla_breached" ? "sla-breached-tab" : ""
                        }
                      >
                        {tab.label} ({tab.count(ticketStats)})
                      </Nav.Link>
                    </Nav.Item>
                  ))}
              </Nav>
              <Tab.Content>
                {/* Tab content will be rendered here */}
                <SupervisorDashboard
                  tickets={ticketsData || []}
                  isLoading={ticketsLoading}
                  activeTab={activeTab}
                  onStartWork={handleStartWork}
                  onCompleteWork={handleCompleteWork}
                  onViewDetails={handleTicketCardClick}
                  isPage={currentPage}
                  setIsPage={(value) => setCurrentPage(value)}
                  pageLimit={pageLimit}
                  setPageLimit={setPageLimit}
                  workingOnTickets={workingOnTickets}
                  tablePagination={ticketsPagination}
                  userInfoData={userInfoData}
                />
              </Tab.Content>
            </Tab.Container>
          </Col>
        </Row>
      )}

      {/* Main Content with Tabs */}
      {isManager && (
        <Row className="mt-4">
          <Col>
            <Tab.Container activeKey={activeTab} onSelect={handleTabChange}>
              <Nav variant="tabs" className="mb-3">
                {Object.values(availableTabs).map((tab) => (
                  <Nav.Item key={tab.key}>
                    <Nav.Link
                      eventKey={tab.key}
                      className={
                        tab.key === "sla_breached" ? "sla-breached-tab" : ""
                      }
                    >
                      {tab.label} ({tab.count(ticketStats)})
                    </Nav.Link>
                  </Nav.Item>
                ))}
              </Nav>

              <Tab.Content>
                {/* Assignment Tab */}
                <Tab.Pane eventKey="assignment">
                  <Card>
                    <Card.Header>
                      <h6 className="mb-0">Ticket Assignment Management</h6>
                    </Card.Header>
                    <Card.Body>
                      <TicketListingWrapper
                        {...commonTicketListingProps}
                        showAssignButton={true}
                        isManagerAssignment={true}
                      />
                    </Card.Body>
                  </Card>
                </Tab.Pane>

                {/* Assigned Tickets Tab */}
                <Tab.Pane eventKey="assigned">
                  <Card>
                    <Card.Header>
                      <h6 className="mb-0">All Assigned Tickets</h6>
                    </Card.Header>
                    <Card.Body>
                      <TicketListingWrapper
                        {...commonTicketListingProps}
                        onVerifyClick={handleOpenVerificationModal}
                      />
                    </Card.Body>
                  </Card>
                </Tab.Pane>

                {/* Open Tab */}
                <Tab.Pane eventKey="open">
                  <Card>
                    <Card.Header>
                      <h6 className="mb-0">Open Tickets</h6>
                    </Card.Header>
                    <Card.Body>
                      <TicketListingWrapper
                        {...commonTicketListingProps}
                        onVerifyClick={handleOpenVerificationModal}
                      />
                    </Card.Body>
                  </Card>
                </Tab.Pane>

                {/* In Progress Tab */}
                <Tab.Pane eventKey="in_progress">
                  <Card>
                    <Card.Header>
                      <h6 className="mb-0">In Progress Tickets</h6>
                    </Card.Header>
                    <Card.Body>
                      <TicketListingWrapper
                        {...commonTicketListingProps}
                        onVerifyClick={handleOpenVerificationModal}
                      />
                    </Card.Body>
                  </Card>
                </Tab.Pane>

                {/* Work Completed Tab */}
                <Tab.Pane eventKey="work_completed">
                  <Card>
                    <Card.Header>
                      <h6 className="mb-0">Work Completed Tickets</h6>
                    </Card.Header>
                    <Card.Body>
                      <TicketListingWrapper
                        {...commonTicketListingProps}
                        onVerifyClick={handleOpenVerificationModal}
                      />
                    </Card.Body>
                  </Card>
                </Tab.Pane>

                {/* Resolved Tab */}
                <Tab.Pane eventKey="resolved">
                  <Card>
                    <Card.Header>
                      <h6 className="mb-0">Resolved Tickets (FD)</h6>
                    </Card.Header>
                    <Card.Body>
                      <TicketListingWrapper
                        {...commonTicketListingProps}
                        onVerifyClick={handleOpenVerificationModal}
                        onMarkAsResolved={handleMarkAsResolved}
                      />
                    </Card.Body>
                  </Card>
                </Tab.Pane>

                {/* Verify and Closed Tab */}
                <Tab.Pane eventKey="verify_and_closed">
                  <Card>
                    <Card.Header>
                      <h6 className="mb-0">Verify and Closed Tickets</h6>
                    </Card.Header>
                    <Card.Body>
                      <TicketListingWrapper
                        {...commonTicketListingProps}
                        onVerifyClick={handleOpenVerificationModal}
                      />
                    </Card.Body>
                  </Card>
                </Tab.Pane>

                {/* Rejected by Manager Tab */}
                <Tab.Pane eventKey="rejected_by_mgr_tickets">
                  <Card>
                    <Card.Header>
                      <h6 className="mb-0">Rejected by Manager Tickets</h6>
                    </Card.Header>
                    <Card.Body>
                      <TicketListingWrapper
                        {...commonTicketListingProps}
                        onVerifyClick={handleOpenVerificationModal}
                      />
                    </Card.Body>
                  </Card>
                </Tab.Pane>

                {/* Rejected Tickets Tab */}
                <Tab.Pane eventKey="rejected_tickets">
                  <Card>
                    <Card.Header>
                      <h6 className="mb-0">Rejected by FD Tickets</h6>
                    </Card.Header>
                    <Card.Body>
                      <TicketListingWrapper
                        {...commonTicketListingProps}
                        onVerifyClick={handleOpenVerificationModal}
                      />
                    </Card.Body>
                  </Card>
                </Tab.Pane>

                {/* Reopened Tab */}
                <Tab.Pane eventKey="reopened">
                  <Card>
                    <Card.Header>
                      <h6 className="mb-0">Reopened Tickets</h6>
                    </Card.Header>
                    <Card.Body>
                      <TicketListingWrapper
                        {...commonTicketListingProps}
                        onVerifyClick={handleOpenVerificationModal}
                      />
                    </Card.Body>
                  </Card>
                </Tab.Pane>

                {/* Manager's Assigned Tickets */}
                <Tab.Pane eventKey="my_assigned">
                  <Card>
                    <Card.Header>
                      <h6 className="mb-0">My Assigned Tickets</h6>
                    </Card.Header>
                    <Card.Body>
                      <SupervisorDashboard
                        tickets={ticketsData || []}
                        isLoading={ticketsLoading}
                        activeTab="assigned"
                        onStartWork={handleStartWork}
                        onCompleteWork={handleCompleteWork}
                        onViewDetails={handleTicketCardClick}
                        isPage={currentPage}
                        setIsPage={(value) => setCurrentPage(value)}
                        pageLimit={pageLimit}
                        setPageLimit={setPageLimit}
                        workingOnTickets={workingOnTickets}
                        tablePagination={ticketsPagination}
                        userInfoData={userInfoData}
                      />
                    </Card.Body>
                  </Card>
                </Tab.Pane>

                {/* Manager's In Progress Tickets */}
                <Tab.Pane eventKey="my_in_progress">
                  <Card>
                    <Card.Header>
                      <h6 className="mb-0">My In Progress Tickets</h6>
                    </Card.Header>
                    <Card.Body>
                      <SupervisorDashboard
                        tickets={ticketsData || []}
                        isLoading={ticketsLoading}
                        activeTab="in_progress"
                        onStartWork={handleStartWork}
                        onCompleteWork={handleCompleteWork}
                        onViewDetails={handleTicketCardClick}
                        isPage={currentPage}
                        setIsPage={(value) => setCurrentPage(value)}
                        pageLimit={pageLimit}
                        setPageLimit={setPageLimit}
                        workingOnTickets={workingOnTickets}
                        tablePagination={ticketsPagination}
                        userInfoData={userInfoData}
                      />
                    </Card.Body>
                  </Card>
                </Tab.Pane>

                {/* Manager's Completed Tickets */}
                <Tab.Pane eventKey="my_completed">
                  <Card>
                    <Card.Header>
                      <h6 className="mb-0">My Completed Tickets</h6>
                    </Card.Header>
                    <Card.Body>
                      <SupervisorDashboard
                        tickets={ticketsData || []}
                        isLoading={ticketsLoading}
                        activeTab="completed"
                        onStartWork={handleStartWork}
                        onCompleteWork={handleCompleteWork}
                        onViewDetails={handleTicketCardClick}
                        isPage={currentPage}
                        setIsPage={(value) => setCurrentPage(value)}
                        pageLimit={pageLimit}
                        setPageLimit={setPageLimit}
                        workingOnTickets={workingOnTickets}
                        tablePagination={ticketsPagination}
                        userInfoData={userInfoData}
                      />
                    </Card.Body>
                  </Card>
                </Tab.Pane>

                {/* Pending Verification Tab */}
                <Tab.Pane eventKey="verification">
                  <Card>
                    <Card.Header>
                      <h6 className="mb-0">Pending Verification</h6>
                    </Card.Header>
                    <Card.Body>
                      <TicketListingWrapper
                        {...commonTicketListingProps}
                        onVerifyClick={handleOpenVerificationModal}
                        onMarkAsResolved={handleMarkAsResolved}
                      />
                    </Card.Body>
                  </Card>
                </Tab.Pane>

                {/* All Tickets Tab */}
                <Tab.Pane eventKey="all">
                  <Card>
                    <Card.Header>
                      <h6 className="mb-0">All Tickets Overview</h6>
                    </Card.Header>
                    <Card.Body>
                      <TicketListingWrapper
                        {...commonTicketListingProps}
                        onVerifyClick={handleOpenVerificationModal}
                      />
                    </Card.Body>
                  </Card>
                </Tab.Pane>

                {/* SLA Breached Tab */}
                <Tab.Pane eventKey="sla_breached">
                  <Card>
                    <Card.Header>
                      <h6 className="mb-0">⚠️ SLA Breached Tickets</h6>
                    </Card.Header>
                    <Card.Body>
                      <TicketListingWrapper
                        {...commonTicketListingProps}
                        onVerifyClick={handleOpenVerificationModal}
                        onMarkAsResolved={handleMarkAsResolved}
                      />
                    </Card.Body>
                  </Card>
                </Tab.Pane>
              </Tab.Content>
            </Tab.Container>
          </Col>
        </Row>
      )}

      {isAdminLike && (
        <Row className="mt-4">
          <Col>
            <AdminDashboard
              handleTabChange={handleTabChange}
              activeTab={activeTab}
              ADMIN_TABS={ADMIN_TABS}
              tickets={ticketsData}
              isLoading={ticketsLoading}
              isPage={currentPage}
              setIsPage={(value) => setCurrentPage(value)}
              pageLimit={pageLimit}
              setPageLimit={setPageLimit}
              tablePagination={ticketsPagination}
              userInfoData={userInfoData}
              onTicketClick={handleTicketCardClick}
              onTicketAction={handleTicketUpdate}
              ticketStats={ticketStats}
              gymFilter={gymFilter}
              supervisorFilter={supervisorFilter}
              handleReload={handleReload}
            />
          </Col>
        </Row>
      )}

      {/* Non-manager view */}
      {isReceptionist && (
        <Row className="mt-4">
          <Col>
            <Tab.Container activeKey={activeTab} onSelect={handleTabChange}>
              <Nav variant="tabs" className="mb-3">
                <Nav.Item>
                  <Nav.Link eventKey="my_tickets">
                    My Tickets ({ticketStats?.total_tickets || 0})
                  </Nav.Link>
                </Nav.Item>
                <Nav.Item>
                  <Nav.Link eventKey="pending_resolution">
                    Pending Resolution (
                    {ticketStats?.work_completed_tickets || 0})
                  </Nav.Link>
                </Nav.Item>
              </Nav>

              <Tab.Content>
                {/* My Tickets Tab */}
                <Tab.Pane eventKey="my_tickets">
                  <Card>
                    <Card.Header>
                      <h6 className="mb-0">My Tickets</h6>
                    </Card.Header>
                    <Card.Body>
                      <TicketListingWrapper
                        {...commonTicketListingProps}
                        onMarkAsResolved={handleMarkAsResolved}
                      />
                    </Card.Body>
                  </Card>
                </Tab.Pane>

                {/* Pending Resolution Tab */}
                <Tab.Pane eventKey="pending_resolution">
                  <Card>
                    <Card.Header>
                      <h6 className="mb-0">Pending Resolution</h6>
                    </Card.Header>
                    <Card.Body>
                      <TicketListingWrapper
                        {...commonTicketListingProps}
                        onMarkAsResolved={handleMarkAsResolved}
                      />
                    </Card.Body>
                  </Card>
                </Tab.Pane>
              </Tab.Content>
            </Tab.Container>
          </Col>
        </Row>
      )}

      {/* Modals */}
      <RaiseTicketModal
        isOpen={isRaiseModalOpen}
        onClose={handleCloseRaiseModal}
        onSubmit={handleTicketSubmit}
        userInfoData={userInfoData}
      />

      <TicketDetailsModal
        isOpen={isTicketDetailsOpen}
        onClose={handleCloseTicketDetails}
        ticket={selectedTicket}
        onTicketUpdate={handleTicketUpdate}
        userInfoData={userInfoData}
        onWriteSuccess={handleWriteSuccess}
        scrollToVerification={scrollToVerification}
        onScrollComplete={() => setScrollToVerification(false)}
      />
    </div>
  );
};

export default TicketDashboardLanding;
